<script setup>
import BaseModalSimple from "@/Components/UI/modals/BaseModalSimple.vue";
import BaseButton from "@/Components/UI/buttons/BaseButton.vue";
import {reactive, ref} from "vue";
import notifyUser from "@/helpers/notifyUser.js";
import parseUserCertificates from "@/helpers/sign/parseUserCertificates.js";
import {useForm} from "@inertiajs/vue3";
import createValidOpensslSignature from "@/helpers/sign/createValidOpensslSignature.js";
import FormError from "@/Components/UI/forms/FormError.vue";
import CertificateListItem from "@/Components/documents/CertificateListItem.vue";
import isValidCryptoProSetup from "@/helpers/sign/isValidCryptoProSetup.js";

defineProps({
    disabled: Boolean,
});

const emit = defineEmits(['update:disabled']);
const showModal = ref(false);
const certificates = reactive({
    list: [],
    chosen: null,
});

const prepareModal = async () => {
    try {
        const validSetup = await isValidCryptoProSetup();
        if (!validSetup) {
            notifyUser('Проверьте настройку плагина для начала работы: https://cryptopro.ru/sites/default/files/products/cades/demopage_webtools/webtools.html');
            return;
        }
    } catch (e) {
        notifyUser('Ошибка при обращении к ЭЦП. Настройте плагин для начала работы: https://cryptopro.ru/sites/default/files/products/cades/demopage_webtools/webtools.html');
        console.error(e);
        return;
    }

    try {
        certificates.list = await parseUserCertificates();
        if (!certificates.list.length) {
            notifyUser('У вас нет действующих сертификатов ЭЦП');
        }
        showModal.value = true;
    } catch (e) {
        notifyUser('Необходим доступ к ЭЦП');
        console.error(e);
    }
}
const form = useForm({
    username: 'unknown',
    token: '',
    password: '',
    remember: true,
});

const submit = async (cert) => {
    emit('update:disabled', true);
    certificates.chosen = cert;
    form.clearErrors();

    let tokenResponse;
    try {
        tokenResponse = await axios.post(route('login.generate_token'), {
            certificate: {
                fingerprint: certificates.chosen.thumbprint,
                subject: {
                    CN: certificates.chosen.subject.CN,
                    INN: certificates.chosen.subject['ИНН'],
                    SNILS: certificates.chosen.subject['СНИЛС'],
                    INNLE: certificates.chosen.subject['ИННЮЛ'],
                },
                issuer: {
                    CN: certificates.chosen.issuer.CN,
                },
            }
        });

        form.token = tokenResponse.data.token;
    } catch (e) {
        notifyUser('При входе по УКЭП произошла ошибка! Пожалуйста, войдите с помощью другой ЭЦП или пароля');
        emit('update:disabled', false);
        throw e;
    }

    try {
        form.password = await createValidOpensslSignature(certificates.chosen.thumbprint, form.token);

    } catch (err) {
        notifyUser('Не удалось выполнить подписание. ' + err?.message);
        console.error(err);
        emit('update:disabled', false);
        return;
    }

    form.post(route('login'), {
        onFinish: () => {
            emit('update:disabled', false);
            form.reset('password');
            form.reset('token');
        },
        preserveScroll: true,
    });
};
</script>

<template>
    <BaseModalSimple :show="showModal"
                     :class="{'cursor-progress': disabled}"
                     max-width="lg"
                     @close="showModal = false">
        <template #title>Вход по УКЭП</template>
        <template #description>
            Выберите подпись из списка для входа.
            <br>
            Для гарантии безопасности потребуется подписать уникальный код.
        </template>
        <template #content>
            <div class="flex flex-col gap-5 my-10">
                <div v-for="certificate in certificates.list"
                     :key="certificate.thumbprint">
                    <CertificateListItem :certificate="certificate"
                                         :class="{'pointer-events-none': disabled}"
                                         full
                                         @click="submit(certificate)"/>
                    <FormError v-if="certificate.thumbprint === certificates.chosen?.thumbprint"
                               :error="form.errors.username"/>
                </div>
            </div>
        </template>
    </BaseModalSimple>

    <div class="flex items-center text-dark-light gap-5 mb-5">
        <span class="bg-dark-light h-px opacity-50 w-full"></span>
        <span class="-mt-1">или</span>
        <span class="bg-dark-light h-px opacity-50 w-full"></span>
    </div>

    <BaseButton variant="secondary"
                         :disabled="disabled"
                         class="w-full mb-5"
                         type="button"
                         @click="prepareModal">
        Войти по УКЭП
    </BaseButton>
</template>
